import React, { useEffect, useState } from 'react';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Send, ExternalLink, TrendingUp, Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog';
import { formatINR } from '@/lib/format';
import { DatePicker } from '@/components/ui/date-picker';

export default function Goldsmiths() {
  const { effectiveCompanyId } = useAuth();
  return (
    <AppShell title="Goldsmiths" subtitle="Skilled artisans your jewellery is manufactured by.">
      <CompanyGate>
        <GoldsmithsContent companyId={effectiveCompanyId} />
      </CompanyGate>
    </AppShell>
  );
}

function GoldsmithsContent({ companyId }) {
  // Wave 3.41 — Restricted users hide value pills.
  const { canViewValues } = useAuth();
  const [list, setList] = useState([]);
  const [openCounts, setOpenCounts] = useState({});
  const [completedCounts, setCompletedCounts] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', contact_person: '', phone: '', email: '', address: '', specialization: '', notes: '', default_credit_days: '' });
  const [delTarget, setDelTarget] = useState(null);
  // Wave 3.36 — Cross-goldsmith pending bills report dialog state.
  const [pendingDlgOpen, setPendingDlgOpen] = useState(false);
  const [pendingCutoff, setPendingCutoff] = useState('');
  const [pendingBusy, setPendingBusy] = useState(false);
  // Phase 15 — company-wide stock value pills (Unattributed + Attributed)
  const [stockValue, setStockValue] = useState({
    total_unattributed_value: 0,
    total_attributed_value: 0,
    total_value: 0,
    goldsmiths_with_balance_count: 0,
  });

  const load = async () => {
    const { data } = await api.get('/masters/goldsmiths', { params: { company_id: companyId } });
    setList(data);
    // Phase 15 — pull aggregate stock value across all goldsmiths in parallel
    try {
      const sv = await api.get('/goldsmith-issues/stock-value-company', { params: { company_id: companyId } });
      setStockValue(sv.data || stockValue);
    } catch (e) {
      // Non-fatal — pills will show ₹0 if the endpoint fails.
    }
    const oc = {}, cc = {};
    // Wave 4.x — PERFORMANCE. Fetch every goldsmith's Open (active) +
    // Completed counts in ONE aggregation request instead of firing two
    // ``/by-goldsmith/{id}`` calls per goldsmith (previously up to ~400
    // heavy requests that stalled the master list on large data sets).
    // ``active`` here matches the Goldsmith Detail default filter exactly.
    try {
      const { data: cnt } = await api.get('/orders/counts/by-goldsmith', { params: { company_id: companyId } });
      const counts = (cnt && cnt.counts) || {};
      for (const g of data) {
        const row = counts[g.id];
        oc[g.id] = row ? (row.active || 0) : 0;
        cc[g.id] = row ? (row.completed || 0) : 0;
      }
    } catch {
      // Non-fatal — columns fall back to 0 if the aggregation fails.
    }
    setOpenCounts(oc); setCompletedCounts(cc);
  };
  useEffect(() => { if (companyId) load(); }, [companyId]);

  const openDialog = (g) => {
    if (g) {
      setEditing(g); setForm({
        name: g.name,
        contact_person: g.contact_person || '',
        phone: g.phone || '',
        email: g.email || '',
        address: g.address || '',
        specialization: g.specialization || '',
        notes: g.notes || '',
        default_credit_days: g.default_credit_days != null ? String(g.default_credit_days) : '',
      });
    } else {
      setEditing(null); setForm({ name: '', contact_person: '', phone: '', email: '', address: '', specialization: '', notes: '', default_credit_days: '' });
    }
    setOpen(true);
  };
  const submit = async () => {
    try {
      const payload = { ...form };
      payload.default_credit_days =
        form.default_credit_days === '' || form.default_credit_days == null
          ? null
          : Math.max(0, parseInt(form.default_credit_days, 10) || 0);
      if (editing) await api.put(`/masters/goldsmiths/${editing.id}`, payload);
      else await api.post('/masters/goldsmiths', { ...payload, company_id: companyId });
      toast.success(editing ? 'Updated' : 'Created'); setOpen(false); load();
    } catch (e) { toast.error(extractError(e, 'Failed')); }
  };

  // Wave 3.36 — Cross-goldsmith Pending Bills download.
  const downloadPendingBills = async () => {
    if (!pendingCutoff) {
      toast.error('Please pick a cutoff date.');
      return;
    }
    setPendingBusy(true);
    try {
      const params = { company_id: companyId, due_on_or_before: pendingCutoff };
      const res = await api.get('/goldsmiths/pending-bills/pdf', { params, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pending_bills_goldsmiths_${pendingCutoff}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Pending bills report downloaded');
      setPendingDlgOpen(false);
    } catch (e) {
      toast.error(extractError(e, 'Failed to generate report'));
    } finally {
      setPendingBusy(false);
    }
  };

  return (
    <div>
      <div className="sticky-filter-bar flex items-start justify-between mb-3 flex-wrap gap-3 rounded-lg px-3" data-testid="goldsmiths-filter-bar">
        <div>
          <div className="text-base font-medium text-[var(--ink-navy)]">Goldsmith Master</div>
          <div className="text-xs text-slate-500">Artisans receiving issues from your inventory.</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Phase 15 — company-wide value pills (Unattributed + Attributed
              across all goldsmiths). Mirrors the per-goldsmith pills on the
              Goldsmith Detail page so the master view tells the same story
              at a glance. Wave 3.41 — pills hidden entirely for users
              without ``values.view``. */}
          {canViewValues && (
            <>
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-amber-200 bg-amber-50 text-amber-800 text-[11px] font-mono tabular-nums"
                title="Total value of material with any goldsmith not yet attributed to an order"
                data-testid="goldsmiths-pill-unattributed"
              >
                <Wallet size={11} className="opacity-80" />
                <span className="text-[10px] uppercase tracking-wide font-sans">Unattributed</span>
                <span className="font-medium" data-testid="goldsmiths-pill-unattributed-amount">{formatINR(stockValue.total_unattributed_value || 0)}</span>
              </span>
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-[11px] font-mono tabular-nums"
                title="Total value attributed to orders that are not Completed (Active / On-Hold / In Progress)"
                data-testid="goldsmiths-pill-attributed"
              >
                <TrendingUp size={11} className="opacity-80" />
                <span className="text-[10px] uppercase tracking-wide font-sans">Attributed</span>
                <span className="font-medium" data-testid="goldsmiths-pill-attributed-amount">{formatINR(stockValue.total_attributed_value || 0)}</span>
              </span>
              <span
                className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-700 text-[11px] font-mono tabular-nums"
                title="Total value of all material currently with goldsmiths"
                data-testid="goldsmiths-pill-total"
              >
                <span className="text-[10px] uppercase tracking-wide font-sans">Total</span>
                <span className="font-medium" data-testid="goldsmiths-pill-total-amount">{formatINR(stockValue.total_value || 0)}</span>
              </span>
            </>
          )}
          <Button variant="outline" onClick={() => { setPendingCutoff(''); setPendingDlgOpen(true); }} data-testid="goldsmiths-pending-bills-button">Pending Bills</Button>
          <Button className="luxury-button" onClick={() => openDialog(null)} data-testid="goldsmith-add-button"><Plus size={14} className="mr-1.5" /> New Goldsmith</Button>
        </div>
      </div>
      <Card className="overflow-hidden">
        <div className="ledger-table overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              <th className="text-left">Goldsmith</th>
              <th className="text-left">Specialization</th>
              <th className="text-left">Phone / Email</th>
              <th className="text-right">Open Orders</th>
              <th className="text-right">Completed</th>
              <th className="text-right">Actions</th>
            </tr></thead>
            <tbody data-testid="goldsmiths-table-body">
              {list.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-500">No goldsmiths yet</td></tr>}
              {list.map((g) => (
                <tr key={g.id} data-testid={`goldsmith-row-${g.id}`}>
                  <td>
                    <Link to={`/goldsmiths/${g.id}`} className="font-medium hover:text-[var(--ink-navy)] hover:underline" data-testid={`goldsmith-open-${g.id}`}>{g.name}</Link>
                    <div className="text-xs text-slate-500">{g.contact_person}</div>
                  </td>
                  <td className="text-sm text-slate-600">{g.specialization}</td>
                  <td className="text-xs text-slate-500">{g.phone}<br/>{g.email}</td>
                  <td className="text-right font-mono tabular-nums">{openCounts[g.id] || 0}</td>
                  <td className="text-right font-mono tabular-nums">{completedCounts[g.id] || 0}</td>
                  <td className="text-right">
                    <Link to={`/goldsmiths/${g.id}`} className="text-xs text-[var(--ink-navy)] hover:underline mr-2 inline-flex items-center" data-testid={`goldsmith-issue-${g.id}`}><Send size={12} className="mr-1" />Issue</Link>
                    <Link to={`/goldsmiths/${g.id}`} className="text-xs text-[var(--ink-navy)] hover:underline mr-2 inline-flex items-center" data-testid={`goldsmith-view-${g.id}`}><ExternalLink size={12} className="mr-1" />View</Link>
                    <button onClick={() => openDialog(g)} className="text-xs text-[var(--ink-navy)] hover:underline mr-2" data-testid={`goldsmith-edit-${g.id}`}><Pencil size={12} className="inline mr-1" />Edit</button>
                    <button onClick={() => setDelTarget(g)} className="text-xs text-red-600 hover:underline" data-testid={`goldsmith-delete-${g.id}`}><Trash2 size={12} className="inline mr-1" />Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Goldsmith' : 'New Goldsmith'}</DialogTitle><DialogDescription>Add an artisan/karigar to your manufacturing network.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="goldsmith-name-input" /></div>
            <div><Label>Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
            <div><Label>Specialization</Label><Input value={form.specialization} onChange={(e) => setForm({ ...form, specialization: e.target.value })} placeholder="e.g. Setting, Stringing" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div>
              <Label>Default Credit Days</Label>
              <Input
                type="number"
                min={0}
                max={365}
                placeholder="e.g. 15 (leave blank for none)"
                value={form.default_credit_days}
                onChange={(e) => setForm({ ...form, default_credit_days: e.target.value.replace(/[^0-9]/g, '') })}
                data-testid="goldsmith-default-credit-days-input"
              />
              <div className="text-[10px] text-slate-500 mt-0.5">Pre-fills credit period on new labour bills.</div>
            </div>
            <div className="col-span-2"><Label>Address</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="luxury-button" data-testid="goldsmith-submit-button">{editing ? 'Save' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wave 3.36 — Cross-goldsmith Pending Bills report */}
      <Dialog open={pendingDlgOpen} onOpenChange={setPendingDlgOpen}>
        <DialogContent className="max-w-md" data-testid="goldsmiths-pending-bills-dialog">
          <DialogHeader>
            <DialogTitle>Pending Bills Report</DialogTitle>
            <DialogDescription>
              Print every outstanding goldsmith labour bill whose due date falls on or before the chosen cutoff, grouped by goldsmith.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs text-slate-500">Due on or before *</Label>
              <DatePicker
                value={pendingCutoff}
                onChange={(e) => setPendingCutoff(e.target.value)}
                data-testid="goldsmiths-pending-bills-cutoff"
              />
              <div className="text-[10px] text-slate-500 mt-0.5">Bills with no due date are excluded.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDlgOpen(false)}>Cancel</Button>
            <Button
              onClick={downloadPendingBills}
              disabled={pendingBusy || !pendingCutoff}
              className="luxury-button"
              data-testid="goldsmiths-pending-bills-download"
            >
              {pendingBusy ? 'Generating…' : 'Download PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {delTarget && (
        <DeleteConfirmDialog
          open={!!delTarget}
          onOpenChange={(v) => { if (!v) setDelTarget(null); }}
          entity_type="goldsmith"
          entity_id={delTarget.id}
          entity_label={delTarget.name}
          company_id={companyId}
          onSuccess={() => { setDelTarget(null); load(); }}
        />
      )}
    </div>
  );
}
